import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import AutofillBanner from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2, Lock, TrendingUp, Palette, FileText, Instagram, ArrowRight, Check, X } from 'lucide-react';

const BrandGlowUpContent = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [glowUp, setGlowUp] = useState(null);
  const [isFullAccess, setIsFullAccess] = useState(false);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage,
    loading: contextLoading 
  } = useUserContext();

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  // During beta (SHOW_PRICING=false), all users have full access
  const hasFullAccess = !SHOW_PRICING || tier === 'premium' || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    fetchLatest();
  }, []);

  const fetchLatest = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/brand-glow-up/latest`, { headers });
      if (response.data.glow_up) {
        setGlowUp(response.data.glow_up);
        setIsFullAccess(response.data.is_full_access);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/brand-glow-up/generate`, {}, { headers });
      setGlowUp(response.data);
      setIsFullAccess(response.data.is_full_access);
      toast.success('Brand Glow Up généré !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la génération');
    } finally {
      setLoading(false);
    }
  };

  const report = isFullAccess ? glowUp?.report || glowUp?.full_report : glowUp?.preview;

  return (
    <DashboardLayout title="Brand Glow Up">
      {/* Auto-fill Banner */}
      {hasContextData && !glowUp && (
        <AutofillBanner
          hasData={hasContextData}
          sources={sources}
          sourcesDisplay={sourcesDisplay}
          message={summaryMessage}
          showDetails={true}
        />
      )}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-luxury p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/10 to-transparent rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">AI-POWERED TRANSFORMATION</span>
            </div>
            
            <h2 className="font-serif text-3xl text-white mb-4">Your Brand Glow Up</h2>
            <p className="text-[#71717A] mb-6 max-w-xl font-light">
              Get a comprehensive brand transformation report with before/after analysis, 
              visual direction, and strategic recommendations.
            </p>
            
            <Button onClick={handleGenerate} disabled={loading} className="btn-gold px-8 py-4 flex items-center gap-2" data-testid="generate-glowup-btn">
              {loading ? (<><Loader2 className="animate-spin" size={18} />Analyzing...</>) : (<><Sparkles size={18} />{glowUp ? 'Regenerate' : 'Generate'} Brand Glow Up</>)}
            </Button>
          </div>
        </motion.div>

        {glowUp && (
          <>
            {/* Unlock Banner for Free Users - Hidden during beta */}
            {SHOW_PRICING && !isFullAccess && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 text-center">
                <Lock className="text-[#D4AF37] mx-auto mb-4" size={40} />
                <h3 className="font-serif text-2xl text-white mb-2">Your Brand Glow Up is Ready</h3>
                <p className="text-[#A1A1AA] mb-6">Unlock the full strategy to see:</p>
                <ul className="text-[#E5C585] space-y-2 mb-8">
                  <li className="flex items-center justify-center gap-2"><Check size={16} /> Your visual identity</li>
                  <li className="flex items-center justify-center gap-2"><Check size={16} /> Your content plan</li>
                  <li className="flex items-center justify-center gap-2"><Check size={16} /> Your feed transformation</li>
                </ul>
                <Button onClick={() => navigate('/dashboard')} className="btn-gold px-10 py-4" data-testid="unlock-glowup-btn">
                  Unlock Full Strategy
                </Button>
              </motion.div>
            )}

            {/* Current Analysis */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">Current Brand Analysis</h3>
                <span className="text-[#52525B] text-sm">(Before)</span>
              </div>
              
              <div className="flex items-center gap-8 mb-6">
                <div className="text-center">
                  <div className="font-serif text-6xl text-[#E5C585]">{report?.current_analysis?.brand_score || '—'}</div>
                  <p className="text-[#52525B] text-sm mt-1">Brand Score</p>
                </div>
                
                {isFullAccess && report?.current_analysis && (
                  <div className="flex-1 grid md:grid-cols-3 gap-4">
                    <div>
                      <h4 className="text-[#D4AF37] text-xs font-accent tracking-wider mb-2">STRENGTHS</h4>
                      <ul className="space-y-1">
                        {report.current_analysis.strengths?.map((s, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                            <Check size={12} className="text-green-400 mt-1" />{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-[#D4AF37] text-xs font-accent tracking-wider mb-2">WEAKNESSES</h4>
                      <ul className="space-y-1">
                        {report.current_analysis.weaknesses?.map((w, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                            <X size={12} className="text-red-400 mt-1" />{w}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-[#D4AF37] text-xs font-accent tracking-wider mb-2">OPPORTUNITIES</h4>
                      <ul className="space-y-1">
                        {report.current_analysis.missed_opportunities?.map((o, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                            <ArrowRight size={12} className="text-[#E5C585] mt-1" />{o}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {isFullAccess && report && (
              <>
                {/* Recommended Identity */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-premium p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="text-[#D4AF37]" size={24} />
                    <h3 className="font-serif text-xl text-white">Recommended Brand Identity</h3>
                    <span className="text-[#D4AF37] text-sm">(After)</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-[#E5C585] text-sm mb-2">Positioning Statement</h4>
                      <p className="text-white font-serif text-lg italic">"{report.recommended_identity?.positioning_statement}"</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-[#E5C585] text-sm mb-1">Brand Archetype</h4>
                        <p className="text-white">{report.recommended_identity?.brand_archetype}</p>
                      </div>
                      <div>
                        <h4 className="text-[#E5C585] text-sm mb-1">Brand Values</h4>
                        <div className="flex gap-2 flex-wrap">
                          {report.recommended_identity?.brand_values?.map((v, i) => (
                            <span key={i} className="px-3 py-1 bg-[#D4AF37]/10 text-[#E5C585] text-sm border border-[#D4AF37]/20">{v}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Visual Direction */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-premium p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Palette className="text-[#E5C585]" size={24} />
                    <h3 className="font-serif text-xl text-white">Visual Direction</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-[#E5C585] text-sm mb-3">Color Palette</h4>
                      <div className="flex gap-3">
                        {report.visual_direction?.color_palette?.map((color, i) => (
                          <div key={i} className="w-16 h-16 border border-[#27272A]" style={{ backgroundColor: color }} />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-[#E5C585] text-sm mb-1">Typography Style</h4>
                        <p className="text-[#A1A1AA] text-sm">{report.visual_direction?.typography_style}</p>
                      </div>
                      <div>
                        <h4 className="text-[#E5C585] text-sm mb-1">Overall Aesthetic</h4>
                        <p className="text-[#A1A1AA] text-sm">{report.visual_direction?.overall_aesthetic}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Content Strategy */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="card-premium p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <FileText className="text-[#D4AF37]" size={24} />
                    <h3 className="font-serif text-xl text-white">Content Strategy</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="text-[#E5C585] text-sm mb-3">Content Pillars</h4>
                      <ul className="space-y-2">
                        {report.content_strategy?.content_pillars?.map((p, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-center gap-2">
                            <div className="w-2 h-2 bg-[#D4AF37]" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-[#E5C585] text-sm mb-3">Content Types</h4>
                      <ul className="space-y-2">
                        {report.content_strategy?.content_types?.map((t, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm">{t}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-[#E5C585] text-sm mb-1">Posting Frequency</h4>
                      <p className="text-white mb-4">{report.content_strategy?.posting_frequency}</p>
                      <h4 className="text-[#E5C585] text-sm mb-1">Storytelling</h4>
                      <p className="text-[#A1A1AA] text-sm">{report.content_strategy?.storytelling_approach}</p>
                    </div>
                  </div>
                </motion.div>

                {/* Feed Transformation */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="card-luxury p-8 mb-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Instagram className="text-[#E5C585]" size={24} />
                    <h3 className="font-serif text-xl text-white">Feed Transformation</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-[#0A0A0A] p-6 border border-red-500/20">
                      <h4 className="text-red-400 text-sm mb-3 font-accent tracking-wider">BEFORE</h4>
                      <ul className="space-y-2">
                        {report.feed_transformation?.before_issues?.map((issue, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                            <X size={14} className="text-red-400 mt-0.5" />{issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-[#0A0A0A] p-6 border border-[#D4AF37]/30">
                      <h4 className="text-[#D4AF37] text-sm mb-3 font-accent tracking-wider">AFTER</h4>
                      <p className="text-white mb-4">{report.feed_transformation?.after_vision}</p>
                      <ul className="space-y-2">
                        {report.feed_transformation?.key_changes?.map((change, i) => (
                          <li key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                            <Check size={14} className="text-[#D4AF37] mt-0.5" />{change}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>

                {/* Action Steps */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="card-gold p-8">
                  <h3 className="font-serif text-xl text-white mb-6">Your Action Steps</h3>
                  <div className="grid md:grid-cols-5 gap-4">
                    {report.action_steps?.map((step, i) => (
                      <div key={i} className="text-center">
                        <div className="w-10 h-10 mx-auto mb-3 bg-gradient-to-br from-[#E5C585] to-[#D4AF37] flex items-center justify-center">
                          <span className="text-black font-serif">{i + 1}</span>
                        </div>
                        <p className="text-[#A1A1AA] text-sm">{step}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </>
        )}

        {!glowUp && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-16 text-center">
            <Sparkles className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">Ready for Your Glow Up?</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">
              Click the button above to generate your personalized brand transformation report.
            </p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const BrandGlowUp = () => {
  return (
    <PrerequisiteGate 
      toolId="brand-glow-up" 
      toolTitle="Brand Glow Up"
    >
      <BrandGlowUpContent />
    </PrerequisiteGate>
  );
};

export default BrandGlowUp;
